import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
	emailSchedulerControllerListConfigsOptions,
	emailSchedulerControllerListConfigsQueryKey,
	emailSchedulerControllerUpsertConfigMutation,
	organizationEmailScheduleControllerListConfigsOptions,
	organizationEmailScheduleControllerListConfigsQueryKey,
	organizationEmailScheduleControllerUpsertConfigMutation,
} from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import type { UpdateEmailScheduleConfigDto } from '@dealstack/shared/lib/backendClient';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import type { EmailScheduleConfigScope } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useEmailScheduleConfig.types';

export const useEmailScheduleConfig = (scope: EmailScheduleConfigScope) => {
	const programmeQuery = useQuery({
		...emailSchedulerControllerListConfigsOptions({
			path: { dealId: scope.dealId ?? '', programmeId: scope.programmeId ?? '' },
		}),
		enabled: !scope.organizationId,
	});

	const organizationQuery = useQuery({
		...organizationEmailScheduleControllerListConfigsOptions({
			path: { organizationId: scope.organizationId ?? '' },
		}),
		enabled: !!scope.organizationId,
	});

	return scope.organizationId ? organizationQuery : programmeQuery;
};

export const useUpsertEmailScheduleConfig = ({
	scope,
	onSuccess,
	onSettled,
}: {
	scope: EmailScheduleConfigScope;
	onSuccess?: () => void;
	onSettled?: () => void;
}) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();

	const invalidate = async () => {
		await queryClient.invalidateQueries({
			queryKey: scope.organizationId
				? organizationEmailScheduleControllerListConfigsQueryKey({
						path: { organizationId: scope.organizationId },
					})
				: emailSchedulerControllerListConfigsQueryKey({
						path: { dealId: scope.dealId ?? '', programmeId: scope.programmeId ?? '' },
					}),
		});
	};

	const shared = {
		onSuccess: async () => {
			showToastV2({
				title: tMipManage('email_scheduler.toast.config_updated.successful'),
				state: 'success',
			});

			await invalidate();

			onSuccess?.();
		},
		onError: () => {
			showToastV2({
				state: 'error',
				title: tMipManage('email_scheduler.toast.config_updated.error'),
			});
		},
		onSettled,
	};

	const programmeMutation = useMutation({
		...emailSchedulerControllerUpsertConfigMutation(),
		...shared,
	});

	const organizationMutation = useMutation({
		...organizationEmailScheduleControllerUpsertConfigMutation(),
		...shared,
	});

	return {
		mutate: (body: UpdateEmailScheduleConfigDto) =>
			scope.organizationId
				? organizationMutation.mutate({
						path: { organizationId: scope.organizationId },
						body,
					})
				: programmeMutation.mutate({
						path: { dealId: scope.dealId ?? '', programmeId: scope.programmeId ?? '' },
						body,
					}),
		isPending: scope.organizationId
			? organizationMutation.isPending
			: programmeMutation.isPending,
	};
};
